/**
 * 规则页要讲清 Milhar 的两件事：**结果是从 D₁..D₁₀ 里读出来的**，以及
 * **比对从末位开始、断了就停**。这两件事都是纯计算，所以放在这里，页面只负责画。
 *
 * <p>窗口（`digitWindow`）由后端 `config` 下发。**不要在页面里写死**：三档并行时
 * 各取一个互不包含的窗口，方案在 doc/MILHAR_MASTER_PRD.md §3.3，改窗口是改
 * `application.yml`，不是发前端。这里的 {@link FALLBACK_WINDOW} 只在接口没返回时
 * 兜底，且必须与后端默认值一致。
 */

import type { MilharTierId } from './tiers.ts';

/** 1-based 闭区间 `[start, end]`，与后端 `config.digitWindow` 同形。 */
export type DigitWindow = [number, number];

/**
 * 接口拿不到 `digitWindow` 时的兜底。
 *
 * <p>与 `MilharProperties` 的结构性默认值一一对应（4 位档 D1-D4、整点档 D5-D9）。
 * 这两个值来自 `application.yml`，不在管理后台的可调范围内 —— 可调的是 RTP 与
 * 入池比例（V237），那两个**必须**走接口，见 {@link payoutLadder}。
 */
export const FALLBACK_WINDOW: Record<MilharTierId, DigitWindow> = {
  '4': [1, 4],
  '5': [5, 9],
  '6': [3, 8],
};

export function normalizeWindow(
  raw: number[] | undefined | null,
  tier: MilharTierId,
): DigitWindow {
  const fallback = FALLBACK_WINDOW[tier] || FALLBACK_WINDOW['4'];
  if (!raw || raw.length !== 2) return fallback;
  const [start, end] = raw;
  if (!Number.isInteger(start) || !Number.isInteger(end)) return fallback;
  if (start < 1 || end > 10 || end < start) return fallback;
  return [start, end];
}

/** D₁..D₁₀ 里属于这个窗口的那几位，拼成开奖号。 */
export function windowResult(digits: readonly number[], window: DigitWindow): string {
  const [start, end] = window;
  if (digits.length < end) return '';
  return digits.slice(start - 1, end).join('');
}

/** 某一位（1-based）是否落在窗口里。 */
export function inWindow(position: number, window: DigitWindow): boolean {
  return position >= window[0] && position <= window[1];
}

/** 换一个**一定不同**的数字，用来造演示号码。不引入随机：规则页每次都该长一样。 */
function otherDigit(digit: string): string {
  return String((Number(digit) + 3) % 10);
}

/**
 * 「中了 N 位」的演示号：保留末 N 位，把再往左那一位改掉。
 *
 * <p>改掉的必须是**紧挨着的那一位**，否则演示号会意外多中几位，页面上标的
 * 「中 2 位」和实际能拿到的钱对不上 —— 规则页写错赔付比不写更糟。
 */
export function demoPick(result: string, hits: number): string {
  const chars = result.split('');
  for (let i = 0; i < chars.length - hits; i += 1) chars[i] = otherDigit(chars[i]);
  return chars.join('');
}

/**
 * 「前面全对、末位错」的反例号。
 *
 * <p>这是规则页上最重要的一格：玩家默认以为「对上几个数字」就算几位，
 * 于是一注 0 元的号会被当成中奖。反例把它按死在纸面上。
 */
export function demoBrokenPick(result: string): string {
  if (!result) return '';
  return result.slice(0, -1) + otherDigit(result.slice(-1));
}

/** 从右往左连续相同的位数。与后端 `MilharDraw.suffixMatchLength` 同一口径。 */
export function suffixMatch(pick: string, result: string): number {
  if (!pick || pick.length !== result.length) return 0;
  let matched = 0;
  for (let i = pick.length - 1; i >= 0; i -= 1) {
    if (pick[i] !== result[i]) break;
    matched += 1;
  }
  return matched;
}

export type LadderRow = {
  /** 中几位。等于 `digits` 时是头奖档。 */
  matched: number;
  /** 固定赔率倍数；头奖档为 null（走奖池，没有倍数）。 */
  multiplier: number | null;
  /** 固定赔付金额；头奖档为 null。 */
  payout: number | null;
};

/**
 * 赔付阶梯。
 *
 * <p>倍数**一律来自接口**：V237 之后运营可以在后台调 RTP，倍数会被整体缩放。
 * 页面写死的话，后台一调，规则页就开始对玩家撒谎，而且没有任何地方会报错。
 * 接口没给某一档就跳过那一行 —— 宁可少一行，也不许猜。
 */
export function payoutLadder(
  multipliers: Record<string, string> | undefined,
  digits: number,
  ticketPrice: number,
): LadderRow[] {
  const rows: LadderRow[] = [];
  for (let matched = 1; matched < digits; matched += 1) {
    const raw = multipliers?.[String(matched)];
    if (raw == null || raw === '') continue;
    const multiplier = Number(raw);
    if (!Number.isFinite(multiplier)) continue;
    rows.push({
      matched,
      multiplier,
      payout: Number.isFinite(ticketPrice) ? multiplier * ticketPrice : null,
    });
  }
  rows.push({ matched: digits, multiplier: null, payout: null });
  return rows;
}
